import type {
  CommandRequest,
  MatchResponse,
  QueueResponse
} from "../../../shared/api/entry-api";

const defaultAPI = "http://localhost:8787";
const SESSION_KEY = "entry.session.token";
const GENERATED_GUEST_ALIAS = /^Guest[1-9][0-9]{0,11}$/;

export type EntryAuthProvider = "apple" | "google" | "guest";

export interface AuthResponse {
  sessionToken: string;
  playerID: string;
  alias: string;
  expiresAt: string;
}

interface APIErrorPayload {
  error?: string;
  code?: string;
}

export function playerIdentity(): { id: string; alias: string } {
  const storedID = localStorage.getItem("entry.player.id");
  const id = storedID && /^[A-Za-z0-9._:-]{8,100}$/.test(storedID)
    ? storedID
    : `web-${crypto.randomUUID()}`;
  localStorage.setItem("entry.player.id", id);
  const alias = localStorage.getItem("entry.player.alias") || "Player";
  return { id, alias };
}

export function saveAlias(alias: string): void {
  localStorage.setItem("entry.player.alias", alias.trim().slice(0, 32) || "Player");
}

export class EntryAPI {
  private readonly baseURL: string;
  private readonly player = playerIdentity();
  private sessionToken: string | null = localStorage.getItem(SESSION_KEY);
  private sessionPromise: Promise<void> | null = null;

  constructor(baseURL = import.meta.env.VITE_ENTRY_API_URL || defaultAPI) {
    this.baseURL = baseURL.replace(/\/$/, "");
    if (this.provider === "guest" && !GENERATED_GUEST_ALIAS.test(this.alias)) this.signOut();
  }

  get playerID(): string { return localStorage.getItem("entry.player.id") || this.player.id; }

  get alias(): string { return localStorage.getItem("entry.player.alias") || "Player"; }

  get provider(): EntryAuthProvider | null {
    const value = localStorage.getItem("entry.auth.provider");
    return value === "apple" || value === "google" || value === "guest" ? value : null;
  }

  async authenticateGoogle(identityToken: string, alias?: string): Promise<void> {
    const auth = await this.authenticateProvider("google", identityToken, alias);
    this.storeSession(auth);
  }

  async authenticateApple(identityToken: string, nonce: string, alias?: string): Promise<void> {
    const auth = await this.authenticateProvider("apple", identityToken, alias, nonce);
    this.storeSession(auth);
  }

  async continueAsGuest(): Promise<void> {
    await this.ensureGuestSession();
  }

  signOut(): void {
    this.sessionToken = null;
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem("entry.player.id");
    localStorage.removeItem("entry.player.alias");
    localStorage.removeItem("entry.auth.provider");
  }

  async deleteAccount(): Promise<void> {
    await this.ensureGuestSession();
    await this.send<{ ok: boolean }>("/v1/account", { method: "DELETE" });
    this.signOut();
  }

  async updateAlias(alias: string): Promise<void> {
    if (this.provider !== "apple" && this.provider !== "google") {
      throw new Error("Sign in with Apple or Google before choosing a nickname.");
    }
    const cleaned = alias.trim().slice(0, 24);
    if (cleaned.length < 3) throw new Error("Nicknames must be 3–24 characters.");
    await this.ensureGuestSession();
    const auth = await this.request<AuthResponse>("/v1/account/profile", {
      method: "PUT",
      body: JSON.stringify({ alias: cleaned })
    });
    this.storeSession(auth);
  }

  async findOpponent(): Promise<QueueResponse> {
    return this.request<QueueResponse>("/v1/matchmaking/queue", { method: "POST" });
  }

  async queueStatus(ticketID: string): Promise<QueueResponse> {
    return this.request<QueueResponse>(`/v1/matchmaking/queue/${ticketID}`);
  }

  async cancelSearch(ticketID: string): Promise<QueueResponse> {
    return this.request<QueueResponse>(`/v1/matchmaking/queue/${ticketID}`, { method: "DELETE" });
  }

  async createInvite(): Promise<MatchResponse> {
    return this.request<MatchResponse>("/v1/matches", { method: "POST" });
  }

  async joinMatch(matchID: string): Promise<MatchResponse> {
    return this.request<MatchResponse>(`/v1/matches/${encodeURIComponent(matchID)}/join`, { method: "POST" });
  }

  async closeMatch(matchID: string): Promise<MatchResponse> {
    return this.request<MatchResponse>(`/v1/matches/${encodeURIComponent(matchID)}`, { method: "DELETE" });
  }

  async readMatch(matchID: string): Promise<MatchResponse> {
    return this.request<MatchResponse>(`/v1/matches/${encodeURIComponent(matchID)}`);
  }

  async command(matchID: string, command: CommandRequest): Promise<MatchResponse> {
    return this.request<MatchResponse>(`/v1/matches/${encodeURIComponent(matchID)}/commands`, {
      method: "POST",
      body: JSON.stringify(command)
    });
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    await this.ensureGuestSession();
    return this.send<T>(path, init);
  }

  private async authenticateProvider(
    provider: "apple" | "google",
    identityToken: string,
    alias?: string,
    nonce?: string
  ): Promise<AuthResponse> {
    const linkingGuest = this.provider === "guest";
    if (linkingGuest) await this.ensureGuestSession();
    const path = linkingGuest ? `/v1/auth/${provider}/link` : `/v1/auth/${provider}`;
    return this.send<AuthResponse>(path, {
      method: "POST",
      body: JSON.stringify({ identityToken, alias, ...(nonce ? { nonce } : {}) })
    });
  }

  private async send<T>(path: string, init: RequestInit = {}, ensureSession = false): Promise<T> {
    if (ensureSession) await this.ensureGuestSession();
    const response = await fetch(`${this.baseURL}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(this.sessionToken
          ? { Authorization: `Bearer ${this.sessionToken}` }
          : {
              "X-Entry-Player-Id": this.player.id,
              "X-Entry-Player-Alias": this.alias
            }),
        ...(init.headers || {})
      }
    });
    const payload = await response.json() as T & APIErrorPayload;
    if (!response.ok) throw new Error(payload.error || `Entry API request failed (${response.status}).`);
    return payload;
  }

  private async ensureGuestSession(): Promise<void> {
    if (this.sessionToken) return;
    if (!this.sessionPromise) {
      this.sessionPromise = (async () => {
        const response = await fetch(`${this.baseURL}/v1/auth/guest`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({})
        });
        const payload = await response.json() as AuthResponse & APIErrorPayload;
        if (!response.ok || !payload.sessionToken) {
          throw new Error(payload.error || `Entry guest sign-in failed (${response.status}).`);
        }
        this.storeSession(payload);
      })().finally(() => {
        this.sessionPromise = null;
      });
    }
    await this.sessionPromise;
  }

  private storeSession(auth: AuthResponse): void {
    this.sessionToken = auth.sessionToken;
    localStorage.setItem(SESSION_KEY, auth.sessionToken);
    localStorage.setItem("entry.player.id", auth.playerID);
    localStorage.setItem("entry.player.alias", auth.alias);
    const provider = auth.playerID.startsWith("apple-")
      ? "apple"
      : auth.playerID.startsWith("google-")
        ? "google"
        : "guest";
    localStorage.setItem("entry.auth.provider", provider);
  }
}
